import { getRhumbLineBearing } from "geolib";

import {
  getLatLngFromRenderState,
  ITrackingRenderState,
  TLatLngTuple,
} from "@ryde/shared";

import { MIN_BEARING_SEPARATION_DEG } from "../route-block-maplibre.constants";

/** True when two samples are far enough apart to describe travel rather than GPS jitter. */
const isSeparated = (
  [fromLat, fromLng]: TLatLngTuple,
  [toLat, toLng]: TLatLngTuple,
): boolean =>
  Math.abs(toLat - fromLat) >= MIN_BEARING_SEPARATION_DEG ||
  Math.abs(toLng - fromLng) >= MIN_BEARING_SEPARATION_DEG;

/**
 * Travel bearing (degrees clockwise from north) at every point of a GPS trail.
 *
 * A stopped vehicle emits runs of identical positions — on a real ride a quarter of the points can be within
 * a metre of their neighbours — and a bearing taken from those is 0 or noise, which would throw a trail dot's
 * perpendicular offset off in an arbitrary direction. So each point takes the direction of the last real
 * movement, carried forward in one pass; the leading stationary run inherits the first real direction.
 *
 * Pure data, independent of zoom and of which points are currently drawn: compute it once per render state,
 * not per decimated dot.
 */
export const resolveTrailBearings = (
  renderState: ITrackingRenderState,
): number[] => {
  const { pointCount } = renderState;
  const bearings = new Array<number>(pointCount).fill(0);

  let previous = getLatLngFromRenderState(renderState, 0) as TLatLngTuple;
  let lastBearing: number | undefined;

  for (let index = 1; index < pointCount; index += 1) {
    const current = getLatLngFromRenderState(
      renderState,
      index,
    ) as TLatLngTuple;

    if (isSeparated(previous, current)) {
      lastBearing = getRhumbLineBearing(
        { latitude: previous[0], longitude: previous[1] },
        { latitude: current[0], longitude: current[1] },
      );
      previous = current;
    }

    bearings[index] = lastBearing ?? 0;
  }

  // Points before the vehicle first moved have no direction of their own — give them the first real one.
  const firstBearing = bearings.find(bearing => bearing !== 0);

  if (firstBearing !== undefined) {
    for (let index = 0; index < pointCount; index += 1) {
      if (bearings[index] === 0) {
        bearings[index] = firstBearing;
        continue;
      }

      break;
    }
  }

  return bearings;
};
